import { TinyColor } from '@ctrl/tinycolor';
import {
  App,
  Button,
  Card,
  DatePicker,
  Flex,
  Modal,
  Result,
  Space,
  Table,
  type TableColumnsType,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import JSZip from 'jszip';
import {
  BadgeAlert,
  CircleAlert,
  ExternalLink,
  Eye,
  Inbox,
  Pencil,
  Printer,
  Trash2,
  WandSparkles,
} from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  catchError,
  concatMap,
  EMPTY,
  filter,
  finalize,
  from,
  map,
  of,
  switchMap,
  tap,
  throwError,
  toArray,
} from 'rxjs';
import PrepSheetConfigurationFormModal from './prep-sheet-configuration-form.modal';
import { displayConfigCheckboxes } from './prep-sheet-filter-fields.component';
import { DataTable } from '../../../components/data-table';
import LoadingSpin from '../../../components/loading-spin';
import SyncSuspense from '../../../components/sync-suspense';
import {
  CycleMenu,
  type Customer,
  type MealCategory,
  PrepSheetConfiguration,
  Site,
} from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import useSecuredApi from '../../../hooks/use-secured-api';
import { useAtomicOperations, useQuery } from '../../../jsonapi-provider';
import { LocalDate } from '../../../utils/local-date';

import { CycleMenuSitesGroupsComponent } from '../../scheduling/cycle-menus/cycle-menu-sites-groups.component';
import { buildParentEntityToSite } from '../../scheduling/cycle-menus/cycles-menu.page';
import type PrepSheetRevision from '../../../entities/prep-sheet-revision';
import type { SortOrder } from 'antd/es/table/interface';

export interface PrepSheetConfigurationFiltersProps {
  onChange?: (filters: FiltersState) => void;
  initialFilters: FiltersState;
}

interface FiltersState {
  query: string;
}

const DEFAULT_SORT_FIELD = 'name';
const DEFAULT_SORT_DIRECTION: SortOrder = 'ascend';
const SUCCESS_DELETE_MESSAGE = 'Preparation Sheet Configuration Deleted Successfully';
const FAILURE_DELETE_MESSAGE = 'Failed To Delete Preparation Sheet Configuration';

const PrepSheetsConfigurationsPage = (): ReactNode => {
  const { modal } = App.useApp();
  const notification = useNotification();
  const { token } = theme.useToken();
  const [reportUrl, setReportUrl] = useState<string | undefined>('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>();
  const pdfSection = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [serviceDate, setServiceDate] = useState<LocalDate | null>(
    searchParams.get('service-date') ? new LocalDate(searchParams.get('service-date')) : null
  );
  const api = useSecuredApi();
  const [prepSheetConfigurationsList, setPrepSheetConfigurationsList] = useState<
    PrepSheetConfiguration[]
  >([]);

  const [isDeleting, setIsDeleting] = useState(false);
  const [configurationToEdit, setConfigurationToEdit] = useState<PrepSheetConfiguration | null>(
    null
  );
  const [parentEntityToSites, setParentEntityToSites] = useState<
    Record<string, Record<string, Site[]>>
  >({});

  const prepSheetConfigurations = useQuery<PrepSheetConfiguration[]>([
    'prep-sheet-configuration',
    {
      sort: DEFAULT_SORT_FIELD,
    },
  ]);

  const prepSheetRevisions = useQuery<PrepSheetRevision[]>(
    serviceDate && [
      'prep-sheet-revision',
      {
        filter: `serviceDate==${serviceDate.toISOString()}`,
        include: ['prepSheetConfiguration'],
        page: {
          size: 9999,
          number: 1,
          totals: 1,
        },
      },
    ]
  );
  const affectedPrepSheetConfigurations = useMemo(() => {
    const latestByConfiguration = new Map<string, PrepSheetRevision>();

    prepSheetRevisions?.data?.forEach((psr) => {
      const configurationId = psr.prepSheetConfiguration.id!;

      const existing = latestByConfiguration.get(configurationId);

      if (
        !existing ||
        new Date(psr.meta?.createdDate).getTime() > new Date(existing.meta?.createdDate).getTime()
      ) {
        latestByConfiguration.set(configurationId, psr);
      }
    });

    return [...latestByConfiguration.values()]
      .filter((psr) => psr.affectedByNewOrderRevision)
      .map((psr) => psr.prepSheetConfiguration.id!);
  }, [prepSheetRevisions?.data]);
  useEffect(() => {
    const configList = prepSheetConfigurations.data ?? [];
    const revisionList = prepSheetRevisions.data ?? [];
    const updatedConfigs = configList.map((config) => ({
      ...config,
      prepSheetRevisions: revisionList.filter((rev) => rev.prepSheetConfiguration.id === config.id),
    }));

    setPrepSheetConfigurationsList(updatedConfigs as PrepSheetConfiguration[]);
  }, [prepSheetConfigurations.data, prepSheetRevisions.data]);

  const columns: TableColumnsType<PrepSheetConfiguration> = [
    { title: '', width: '50px' },
    {
      title: 'Name',
      dataIndex: ['name'],
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
      render: (value, item) => (
        <Space>
          <Typography.Text>{value}</Typography.Text>
          {item.prepSheetRevisions
            ?.toSorted(
              (a, b) =>
                new Date(b.meta?.createdDate).getTime() - new Date(a.meta?.createdDate).getTime()
            )
            .at(0)?.affectedByNewOrderRevision && (
            <Tooltip title="New Changes are affecting this configuration">
              <BadgeAlert color="red" />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      dataIndex: 'site',
      width: '30%',
      title: (
        <Flex vertical>
          <Typography.Text disabled>Sites</Typography.Text>
          <Flex gap="middle" wrap="wrap" align="center">
            <Flex align="center">
              <div className="circle" style={{ background: token.colorPrimary }} />
              <small>Agency</small>
            </Flex>
            <Flex align="center">
              <div
                className="circle"
                style={{
                  background: new TinyColor(token.colorPrimary).setAlpha(0.3).toHslString(),
                }}
              />
              <small>County</small>
            </Flex>
            <Flex align="center">
              <div
                className="circle"
                style={{
                  background: new TinyColor(token.colorPrimary).setAlpha(0.1).toHslString(),
                }}
              />
              <small>Customer</small>
            </Flex>
            <Flex align="center">
              <div
                className="circle"
                style={{
                  border: `1px solid var(--ant-color-primary)`,
                }}
              />
              <small>Site</small>
            </Flex>
          </Flex>
        </Flex>
      ),
      render: (value: string[]) => {
        if (!value?.length) return <Tag color={token.colorPrimary}>All Sites</Tag>;
        const resolvedSites = value
          .map((id) => allSites.find((s) => s.id === id))
          .filter(Boolean) as Site[];
        return (
          <CycleMenuSitesGroupsComponent
            cycleMenu={Object.assign(new CycleMenu(), { sites: resolvedSites })}
            parentEntityToSites={parentEntityToSites}
          />
        );
      },
    },
    {
      title: 'Meal Categories',
      dataIndex: ['mealCategory'],
      render: (value: string[]) =>
        value?.map((mealCategoryId) => (
          <Tag key={mealCategoryId} color="processing">
            {mealCategories.data?.find(({ id }) => id === mealCategoryId)?.name ??
              '[DELETED MEAL CATEGORY]'}
          </Tag>
        )) ?? <Tag color={token.colorPrimary}>All Meal Categories</Tag>,
    },
    {
      title: 'Main Types',
      dataIndex: ['mainType'],
      render: (value: string[]) =>
        value?.map((mainType) => (
          <Tag key={mainType} color="processing">
            {mainType.toUpperCase()}
          </Tag>
        )) ?? <Tag color={token.colorPrimary}>All Main Types</Tag>,
    },
    {
      title: 'Display Config',
      dataIndex: ['flags'],
      render: (value: string[]) => (
        <Tooltip
          title={
            <Flex vertical>
              {value
                .filter((f) => !f.includes('.'))
                .map((reportName) =>
                  renderReportTag(
                    reportName,
                    value.filter((v) => v !== reportName && v.startsWith(reportName))
                  )
                )}
            </Flex>
          }
        >
          Summary...
        </Tooltip>
      ),
    },
  ];
  const renderReportTag = (reportName: string, children: string[]): ReactNode => {
    const report = displayConfigCheckboxes.find((el) => el.key === reportName);
    return (
      <Flex vertical key={reportName}>
        <Flex>
          <Typography.Title level={5} style={{ marginBlock: '0px' }}>
            {report?.title.replace('Show ', '')}
          </Typography.Title>
        </Flex>
        <Flex wrap gap="small">
          {children.map((child) => {
            const childReport =
              report?.children?.find((el) => el.key === child) ??
              report?.children
                ?.find((el) => el.children !== undefined)
                ?.children?.find((el) => el.key === child);
            return (
              <Tag color="processing" key={child}>
                {childReport?.title.replace('Include ', '')}
              </Tag>
            );
          })}
        </Flex>
      </Flex>
    );
  };

  const printSheet = (): void => {
    const hiddenFrame = document.createElement('iframe');
    hiddenFrame.onload = (): void => {
      const closePrint = (): void => {
        document.body.removeChild(hiddenFrame);
      };
      hiddenFrame.contentWindow!.onbeforeunload = closePrint;
      hiddenFrame.contentWindow!.onafterprint = closePrint;
      hiddenFrame.contentWindow!.print();
    };
    hiddenFrame.style.display = 'none'; // hide iframe
    hiddenFrame.src = reportUrl ?? '';
    document.body.appendChild(hiddenFrame);
  };

  const openInNewWindow = (): void => {
    window.open(reportUrl);
  };

  const customers = useQuery<Customer[]>([
    'customer',
    {
      filter: 'isInactive==false',
      include: ['sites.agency'],
      sort: 'name',
      fields: {
        customer: ['name', 'sites'],
        site: ['name', 'state', 'county', 'agency'],
        agency: ['name'],
      },
      page: {
        limit: 10000,
      },
    },
  ]);

  const allSites: Site[] = useMemo(
    () =>
      customers.data
        ?.map((cust) =>
          cust.sites.map((site) => Object.assign(new Site(), site, { customer: cust }))
        )
        .flat() ?? [],
    [customers.data]
  );

  const atomicOperations = useAtomicOperations();

  const mealCategories = useQuery<MealCategory[]>([
    'meal-category',
    {
      fields: {
        'meal-category': ['name'],
      },
      sort: 'sortOrder',
      page: {
        size: 10000,
      },
    },
  ]);

  useEffect(() => {
    setParentEntityToSites(
      buildParentEntityToSite(
        customers.data?.flatMap((customer) =>
          customer.sites.map((site) => Object.assign(new Site(), site, { customer }))
        ) ?? []
      )
    );
  }, [customers.data]);

  useEffect(() => {
    customers.error &&
      notification.error({
        message: ' ',
        description: customers.error?.detail,
      });
  }, [customers.error]);

  const fetchSheetFromTableExport = (
    configuration: PrepSheetConfiguration,
    orderDate: LocalDate,
    savePrint: boolean = false
  ): void => {
    from(
      api.post(
        process.env.REACT_APP_PREP_SHEET_ENDPOINT + '/configuration',
        { serviceDate: orderDate, configurationId: configuration.id!, saveRevision: savePrint },
        {},
        {},
        true
      )
    )
      .pipe(
        tap(({ blob }) => {
          const url = URL.createObjectURL(blob);
          setReportUrl(url);

          pdfSection.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }),
        finalize(() => {
          prepSheetRevisions?.refetch?.();
          prepSheetConfigurations?.refetch?.();
        }),
        catchError((error: Error) => {
          setReportUrl('');
          const [description, title] = error?.message.split('\n');
          notification.error({
            description: description ?? 'No sheets available',
            message: title ?? '',
          });
          return EMPTY;
        })
      )
      .subscribe();
  };
  const fetchSheetFByRevisionNumber = (
    configuration: PrepSheetConfiguration,
    orderDate: LocalDate,
    revisionId?: string
  ): void => {
    from(
      api.get(
        process.env.REACT_APP_PREP_SHEET_ENDPOINT + '/prep-sheet-revision/' + revisionId,
        {},
        {},
        true
      )
    )
      .pipe(
        tap(({ blob }) => {
          const url = URL.createObjectURL(blob);
          setReportUrl(url);

          pdfSection.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }),
        finalize(() => {
          prepSheetRevisions?.refetch?.();
          prepSheetConfigurations?.refetch?.();
        }),
        catchError((error: Error) => {
          setReportUrl('');
          const [description, title] = error?.message.split('\n');
          notification.error({
            description: description ?? 'No sheets available',
            message: title ?? '',
          });
          return EMPTY;
        })
      )
      .subscribe();
  };

  const promptConfigurationOrderDate = (record: PrepSheetConfiguration, save: boolean): void => {
    void modal.confirm({
      width: '50%',
      title: (save ? 'Generate' : 'View') + ' Preparation Sheet',
      content: (
        <>
          <Typography.Text>
            {save ? 'Generate' : 'View'} preparation sheet <b>{record.name}</b> on{' '}
            <b>{serviceDate?.toISOString()}</b>
          </Typography.Text>
          {save && <Typography.Text> and save the Generation time?</Typography.Text>}
        </>
      ),
      icon: <></>,
      onOk: () => {
        if (serviceDate === null) {
          notification.error({
            message: 'Error',
            description: 'Please select a service date before generating the preparation sheet.',
          });
          return;
        }
        const config = Object.assign(
          new PrepSheetConfiguration(
            record.name,
            record.flags,
            record.site,
            record.mealCategory,
            record.mainType
          ),
          record
        );
        save
          ? fetchSheetFromTableExport(config, serviceDate, true)
          : fetchSheetFromTableExport(config, serviceDate, false);
      },
      okText: 'Confirm',
      cancelText: 'Cancel',
      okButtonProps: {
        form: 'prep-sheet-configuration-date-form',
        htmlType: 'submit',
        type: 'primary',
      },
    });
  };

  const bulkGenerateReports = (savePrints: boolean): void => {
    if (serviceDate === null) {
      notification.error({
        message: 'Error',
        description: 'Please select a service date before generating the preparation sheet.',
      });
      return;
    }
    const reportsIds = [...selectedRowKeys!];
    void modal.confirm({
      width: '50%',
      title: 'Generate Preparation Sheets',
      icon: <></>,
      content: (
        <Typography.Text>
          Generate <strong>{selectedRowKeys?.length ?? 0}</strong> preparation sheets on{' '}
          {serviceDate?.toISOString()} and save the generation time?
        </Typography.Text>
      ),
      onOk: () => {
        const configurations = reportsIds
          .map((id) => prepSheetConfigurations.data!.find((conf) => conf.id === id)!)
          .map((conf) => {
            const config = new PrepSheetConfiguration(
              conf.name,
              conf.flags,
              conf.site,
              conf.mealCategory,
              conf.mainType
            );
            config.id = conf.id;
            return config;
          });

        from(configurations)
          .pipe(
            concatMap((config, index) => {
              return from(
                api.post(
                  process.env.REACT_APP_PREP_SHEET_ENDPOINT + '/configuration',
                  {
                    serviceDate,
                    configurationId: config.id!,
                    saveRevision: savePrints,
                  },
                  {},
                  {},
                  true
                )
              ).pipe(
                map(({ blob }) => ({
                  blob: blob.slice(0, blob.size, 'application/pdf'),
                  name: configurations[index]?.name ?? `Report-${index + 1}`,
                })),
                catchError(() =>
                  of({
                    blob: null,
                    name: configurations[index]?.name ?? `Report-${index + 1}`,
                  })
                )
              );
            }),
            filter(({ blob }) => !!blob),
            map(({ blob, name }) => ({
              filename: `${name.replaceAll(' ', '_')}-${serviceDate?.toISOString()}.pdf`,
              blob,
            })),
            toArray(),
            switchMap((files: any) => {
              if (files.length === 0) {
                return throwError(() => new Error('No reports available'));
              }
              return from(zipReports(files));
            }),
            finalize(() => {
              prepSheetRevisions?.refetch?.();
            }),
            catchError((error: Error) => {
              const [description, title] = error?.message.split('\n');
              notification.error({
                description: description ?? 'No reports available',
                message: title ?? '',
              });
              return EMPTY;
            })
          )
          .subscribe();
      },

      okText: 'Generate',
      cancelText: 'Cancel',
      okButtonProps: {
        form: 'prep-sheet-configuration-date-form',
        htmlType: 'submit',
        type: 'primary',
      },
    });
  };
  const allAffectedRowsSelected =
    affectedPrepSheetConfigurations.length === (selectedRowKeys ?? []).length &&
    affectedPrepSheetConfigurations.every((id) => selectedRowKeys?.includes(id));

  const selectAllAffectedRows = (): void => {
    setSelectedRowKeys(affectedPrepSheetConfigurations);
  };
  const zipReports = async (files: Array<{ filename: string; blob: Blob }>): Promise<void> => {
    const zip = new JSZip();
    files.forEach((file) => {
      zip.file(file.filename, file.blob, { binary: true });
    });
    await zip.generateAsync({ type: 'blob', streamFiles: true }).then((content) => {
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `preparation_sheets-${dayjs().local().toISOString()}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    });
  };
  const innerColumns = [
    {
      title: 'version Number',
      dataIndex: 'revisionNumber',
      render: (value: any, item: PrepSheetRevision & { reversedIndex: number }) => (
        <Typography.Text>{`#${item.reversedIndex}`}</Typography.Text>
      ),
    },
    {
      title: 'Created At',
      dataIndex: ['meta', 'createdDate'],
      render: (value: string) => (
        <Typography.Text>{value && new Date(value).toLocaleString()}</Typography.Text>
      ),
    },
    {
      title: 'Actions',
      dataIndex: ['meta', 'createdDate'],
      render: (_: any, record: PrepSheetRevision & { reversedIndex: number }) => (
        <Tooltip title="View Version">
          <Button
            type="link"
            icon={<Eye />}
            onClick={() => {
              void modal.confirm({
                width: '50%',
                title: 'View Preparation Sheet',
                content: (
                  <>
                    <Typography.Text>
                      View <b>#{record.reversedIndex}</b> preparation sheet on{' '}
                      <b>{serviceDate?.toISOString()}</b>
                    </Typography.Text>
                  </>
                ),
                icon: <></>,

                onOk: () => {
                  const config = Object.assign(
                    new PrepSheetConfiguration(
                      record.prepSheetConfiguration.name,
                      record.prepSheetConfiguration.flags,
                      record.prepSheetConfiguration.site,
                      record.prepSheetConfiguration.mealCategory,
                      record.prepSheetConfiguration.mainType
                    ),
                    record.prepSheetConfiguration
                  );
                  fetchSheetFByRevisionNumber(config, record.serviceDate, record.id);
                },
                okText: 'Confirm',
                cancelText: 'Cancel',
                okButtonProps: {
                  form: 'prep-sheet-configuration-date-form',
                  htmlType: 'submit',
                  type: 'primary',
                },
              });
            }}
          />
        </Tooltip>
      ),
    },
  ];

  const handlePrepSheetConfigurationDeletion = (record: PrepSheetConfiguration): void => {
    //  update loading state
    setIsDeleting(true);

    from(prepSheetConfigurations.client.delete(['prep-sheet-configuration', record.id]))
      .pipe(
        switchMap((response) => {
          if (response.error || response.errors) {
            return throwError(() => new Error(FAILURE_DELETE_MESSAGE));
          }
          return of(response);
        }),
        tap(() => {
          notification.success({
            message: ' ',
            description: SUCCESS_DELETE_MESSAGE,
          });
        }),
        catchError((error: Error) => {
          notification.error({
            message: ' ',
            description: error.message,
          });
          return EMPTY;
        }),
        finalize(() => {
          setIsDeleting(false);
        })
      )
      .subscribe();
  };
  return (
    <Flex gap="middle" vertical>
      <Flex gap="middle" style={{ width: '100%' }} vertical>
        <DatePicker
          style={{ maxWidth: 200 }}
          allowClear={false}
          value={serviceDate?.toDayjs()}
          placeholder="Select service date"
          onChange={(date) => {
            setServiceDate(new LocalDate(date));
            searchParams.set('service-date', new LocalDate(date).toISOString());
            setSearchParams(searchParams);
          }}
        />
        <DataTable<PrepSheetConfiguration>
          title="Preparation Sheet Configuration"
          description="List of saved configuration for preparation sheets"
          key={'id'}
          selectMode="multiple"
          dataList={prepSheetConfigurationsList}
          dataSource={prepSheetConfigurationsList}
          loading={
            isDeleting || prepSheetConfigurations.isFetching || prepSheetRevisions.isFetching
          }
          onChange={() => {}}
          pagination={false}
          columns={columns}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                rowKey={'id'}
                columns={innerColumns}
                dataSource={record.prepSheetRevisions
                  ?.sort(
                    (a, b) =>
                      new Date(b.meta?.createdDate).getTime() -
                      new Date(a.meta?.createdDate).getTime()
                  )
                  .map((rev, idx) => ({
                    ...rev,
                    reversedIndex: (record.prepSheetRevisions?.length ?? 0) - idx,
                  }))}
                pagination={false}
                size="small"
              />
            ),
            rowExpandable: (record) => (record?.prepSheetRevisions?.length ?? 0) > 0,
          }}
          actions={[
            {
              key: 'view',
              label: 'View',
              icon: <Eye />,
              onItemClick: (record) => {
                promptConfigurationOrderDate(record, false);
              },
            },
            {
              key: 'generate-and-save',
              label: 'Generate',
              icon: <WandSparkles />,
              onItemClick: (record) => {
                promptConfigurationOrderDate(record, true);
              },
            },
            {
              key: 'edit',
              label: 'Edit',
              icon: <Pencil />,
              onItemClick: (record) => {
                setConfigurationToEdit(record);
              },
            },
            {
              key: 'delete',
              label: 'Delete',
              icon: <Trash2 />,
              danger: true,
              onItemClick: (record) => {
                Modal.confirm({
                  width: '50%',
                  title: 'Confirm deletion',
                  icon: <CircleAlert />,
                  content: (
                    <Typography.Text>
                      Are you sure you want to delete <b>{record.name}</b> permanently?
                    </Typography.Text>
                  ),
                  okText: 'Yes',
                  okType: 'danger',
                  cancelText: 'No',
                  onOk: () => {
                    handlePrepSheetConfigurationDeletion(record);
                  },
                });
              },
            },
          ]}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={(selectedRowKeys) => {
            setSelectedRowKeys(selectedRowKeys.map((key) => key.toString()));
          }}
          bulkActionsNode={
            <Flex gap="small">
              {selectedRowKeys && selectedRowKeys.length > 0 && (
                <Button
                  type="primary"
                  icon={<WandSparkles />}
                  loading={atomicOperations.isLoading}
                  disabled={atomicOperations.isLoading}
                  onClick={() => {
                    bulkGenerateReports(true);
                  }}
                >
                  Generate {selectedRowKeys?.length ?? 0} report
                  {(selectedRowKeys?.length ?? 0) > 1 ? 's' : ''}
                </Button>
              )}
              {!allAffectedRowsSelected && (
                <Button type="primary" onClick={selectAllAffectedRows}>
                  Select Affected Rows Only ({affectedPrepSheetConfigurations.length})
                </Button>
              )}
            </Flex>
          }
        />
      </Flex>
      <Flex gap="middle" style={{ width: '100%' }}>
        <Flex ref={pdfSection} flex="1" style={{ maxWidth: 600 }} vertical>
          <Card
            styles={{
              body: reportUrl != null ? { padding: 0 } : undefined,
            }}
            actions={[
              <Flex
                key="actions"
                gap="small"
                style={{ flexDirection: 'row-reverse', paddingInline: token.paddingSM }}
              >
                <Button block icon={<Printer />} onClick={printSheet} disabled={!reportUrl?.length}>
                  Print
                </Button>
                <Button
                  block
                  icon={<ExternalLink />}
                  onClick={openInNewWindow}
                  disabled={!reportUrl?.length}
                >
                  Open in new window
                </Button>
              </Flex>,
            ]}
          >
            <SyncSuspense fallback={<LoadingSpin />} condition={reportUrl != null}>
              <Flex gap="small" justify="center" flex="1 1 100%" vertical>
                <SyncSuspense
                  fallback={
                    <Result
                      title={<Typography.Title level={4}>Nothing to preview</Typography.Title>}
                      icon={<Inbox />}
                      style={{ fontSize: '3em' }}
                    />
                  }
                  condition={!!reportUrl && reportUrl?.length}
                >
                  <iframe
                    src={reportUrl}
                    style={{ border: 0, height: 'auto', aspectRatio: '210 / 297' }}
                    width="100%"
                  />
                </SyncSuspense>
              </Flex>
            </SyncSuspense>
          </Card>
        </Flex>
      </Flex>
      {configurationToEdit && (
        <PrepSheetConfigurationFormModal
          configuration={configurationToEdit}
          onCancel={() => {
            setConfigurationToEdit(null);
          }}
          onSuccess={() => {
            setConfigurationToEdit(null);
            void prepSheetConfigurations.refetch?.();
          }}
        />
      )}
    </Flex>
  );
};

export default PrepSheetsConfigurationsPage;
